import * as React from "react";
import {useEffect, useState} from "react";
import {
    IPortfolio,
    PortfolioApi, IPortfolioComparison, IPerformanceMetrics
} from "../../api/PortfolioApi";
import {ErrorComponent} from "../base/Error.component";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {useSelector} from "react-redux";
import {hasClientModelPortfolioAccess, insightsEnabled} from "../../../mainReducerMapSelectors";
import {Link} from "react-router-dom";
import {PortfolioComparison} from "./PortfolioComparison";
import {ModelPerformanceMetrics} from "./ModelPerformanceMetrics";
import {hasAccessToModelPortfolioPage, hasValidModelPortfolioData} from "./portfolioUtil";
import {hasConsultantLevelAccess, isClientUser} from "../../utils/sessionUtil";
import {IApplicationRootState} from "../../../applicationState";

export const CMA_LABEL = "Capital Market Assumptions";

export const ModelPortfolioPage: React.FunctionComponent = () => {
    const [portfolioComparison, setPortfolioComparison] = useState<IPortfolioComparison | undefined>(undefined);
    const [performanceMetrics, setPerformanceMetrics] = useState<IPerformanceMetrics | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorPage, setErrorPage] = useState<boolean>(false);
    const [selectedModelPortfolio, setSelectedModelPortfolio] = useState<IPortfolio | undefined>(undefined);
    const hasInsightsEnabled = useSelector((state: IApplicationRootState) => insightsEnabled(state));
    const clientModelPortfolioAccessFromStore =
        useSelector((state: IApplicationRootState) => hasClientModelPortfolioAccess(state));

    useEffect( () => {
        if (!(hasConsultantLevelAccess() || (isClientUser() && clientModelPortfolioAccessFromStore))) {
            setErrorPage(true);
            return;
        }
        const fetchData = async () => {
            try {
                const performanceMetricsResponse = await PortfolioApi.getPerformanceMetrics();
                if (!performanceMetricsResponse) throw new Error();
                setPerformanceMetrics(performanceMetricsResponse);

                const portfolioComparisonResponse = await PortfolioApi.getCurrentPlanPortfolioComparison();
                if(!hasValidModelPortfolioData(performanceMetricsResponse, portfolioComparisonResponse)) {
                    throw new Error();
                }
                setPortfolioComparison(portfolioComparisonResponse);
                setSelectedModelPortfolio(portfolioComparisonResponse.modelPortfolios[0]);
                setIsLoading(false);
            } catch {
                setErrorPage(true);
            }
        };
        fetchData();
    }, []);

    const renderModelPortfolioIntroduction = () => {
        return <div data-testid={"model-portfolio-page__introduction-container"}>
            <h2>Introducing our Model Portfolios</h2>
            <p>
                Explore our diversified model portfolios, each tailored to different risk and return profiles.
                Our portfolios are constructed using a strategic approach that balances growth opportunities with risk
                management, ensuring that the investments are aligned with long-term objectives. We meticulously design
                these portfolios with an optimal mix of asset classes,
                based on our {renderCMAs()} (CMAs), to maximize returns while effectively managing risk.
            </p>
        </div>;
    };

    const renderCMAs = () => {
        return hasInsightsEnabled
            ? <Link className={"model-portfolio-page__link"} to="/market-outlooks?document-type=CMA">{CMA_LABEL}</Link>
            : <span>{CMA_LABEL}</span>;
    };
    const renderPortfolioComparisonText = () => {
        return <div data-testid={"model-portfolio-page__comparison-container"}>
            <h2>Compare your Portfolio</h2>
            <p>
                By comparing your current portfolio to our model portfolios, you can see how diversification may lead
                to the potential for higher returns and lower risk. Our calculations demonstrate the potential benefits
                of aligning your investments with our model portfolios.
                <br/>
                <br/>
                Use the comparison tool below to evaluate your portfolio's expected returns based on its current
                allocations and explore how reallocating your investments might enhance your financial strategy.
            </p>
        </div>;
    };

    const getModelPortfolioClass = (modelPortfolio: IPortfolio): string => {
        return selectedModelPortfolio && modelPortfolio.id === selectedModelPortfolio.id
            ? "model-portfolio-page__model-portfolio-item-selected"
            : "model-portfolio-page__model-portfolio-item";
    };

    const renderModelPortfolios = () => {
        return <div className="model-portfolio-page__model-portfolios-container"
                    data-testid="model-portfolio-page__model-portfolios-container">
            <h3>{portfolioComparison?.planType}</h3>
            <div className="model-portfolio-page__model-portfolios">
                {portfolioComparison?.modelPortfolios.map((modelPortfolio: IPortfolio) => (
                    <div key={modelPortfolio.id}
                         className={getModelPortfolioClass(modelPortfolio)}
                         onClick={() => setSelectedModelPortfolio(modelPortfolio)}>
                        {modelPortfolio.name}
                    </div>
                ))}
            </div>
        </div>;
    };

    const getSelectedModelPortfolio = (): IPortfolio => {
        return portfolioComparison!.modelPortfolios
            .find((element) => element.id === selectedModelPortfolio?.id) as IPortfolio;
    };

    const renderPerformanceMetrics = () => {
        if (!performanceMetrics || !selectedModelPortfolio) {
            return null;
        }

        return <ModelPerformanceMetrics
            selectedModelPortfolio={selectedModelPortfolio}
            performanceMetrics={performanceMetrics}
        />;
    };

    const renderComponent = () => {
        if (errorPage) {
            return <ErrorComponent/>;
        }

        if (isLoading) {
            return <LoadingSpinner/>;
        }

        if (!hasAccessToModelPortfolioPage(portfolioComparison!.planType, clientModelPortfolioAccessFromStore)) {
            return <ErrorComponent/>;
        }

        return <div className="main-content new-common-styles model-portfolio-page">
            <h1>Model Portfolios</h1>
            {renderModelPortfolioIntroduction()}
            {renderPortfolioComparisonText()}
            <div className={"model-portfolio-page__comparison-portfolios-container"}>
                {renderModelPortfolios()}
                <div className={"model-portfolio-page__comparison-metrics-container"}>
                    {selectedModelPortfolio
                        && <PortfolioComparison
                            currentPlanPortfolio={portfolioComparison!.currentPlanPortfolio}
                            modelPortfolio={getSelectedModelPortfolio()}
                            asOfDate={portfolioComparison!.asOfDate}/>
                    }
                    {renderPerformanceMetrics()}
                </div>

            </div>
        </div>;
    };

    return renderComponent();
};